import { memo, useEffect, useState } from 'react';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import { ThemeBgSensitive } from '@mentimeter/ragnar-ui/themes';
import {
  QfaLoader,
  QfaNavigationButton,
  QfaTitle,
  QfaTransitionWrapper,
  QfaUpvotes,
} from '@mentimeter/slides-presentation-ui/qfa';
import { FlexibleText } from '@mentimeter/slides-presentation-ui/text/flexible-text';
import { useRagnar } from '@mentimeter/ragnar-react';
import { useTranslations } from 'next-intl';
import { usePresentationContext } from '../../../legacy-context/presentationContext';
import { Hotkey } from '../../../shared/components/Hotkey';
import { SlideControlsButton } from './SlideControlsButton';

const BREAKPOINTS = [
  { count: 225, offset: -3 },
  { count: 150, offset: -2 },
  { count: 50, offset: -1 },
];

interface QuestionT {
  id: number;
  qfaQuestion: string;
  numberOfUpvotes: number;
  intercomEnabled: boolean;
  currentIndex: number;
  totalNrOfQuestions: number;
  isAnswered: boolean;
  loading: boolean;
  askedOnSlideTitle?: string;
  displayName: string | null;
}

export const Question = memo<QuestionT>(
  ({
    id,
    qfaQuestion,
    numberOfUpvotes,
    intercomEnabled,
    currentIndex,
    isAnswered,
    totalNrOfQuestions,
    loading,
    askedOnSlideTitle,
    displayName,
  }: QuestionT) => {
    const {
      Utils: { fontOffset },
      useQfa,
      useConfig,
    } = usePresentationContext();

    const {
      theme: {
        colors: { bg },
      },
    } = useRagnar();

    const {
      actions: { markAsAnswered, nextQuestion, previousQuestion },
    } = useQfa();
    const { readonly } = useConfig();

    const translate = useTranslations('presenting');

    const [answeredQuestion, setAnsweredQuestion] = useState(false);

    useEffect(() => {
      setAnsweredQuestion(isAnswered);
    }, [isAnswered, id]);

    const handleAnswerQuestion = () => {
      setAnsweredQuestion(true);
      setTimeout(() => {
        markAsAnswered(id);
      }, 1000);
    };

    const previousQuestionEnabled = currentIndex > 0;
    const nextQuestionEnabled = currentIndex < totalNrOfQuestions - 1;

    return (
      <Box
        height="100%"
        width="100%"
        alignItems="center"
        justifyContent="space-between"
        extend={({ theme }) => ({
          opacity: totalNrOfQuestions === 0 ? 0 : 1,
          transition: `opacity ${theme.durations[1]}s ease`,
        })}
      >
        <QfaLoader shouldShow={loading} />
        {!readonly && (
          <>
            {previousQuestionEnabled && (
              <Hotkey keyCode="arrowup" action={() => previousQuestion()} />
            )}
            <ThemeBgSensitive themeBg={bg}>
              <QfaNavigationButton
                isDisabled={!previousQuestionEnabled}
                onClick={() => previousQuestion()}
                direction="up"
              />
            </ThemeBgSensitive>
          </>
        )}

        <QfaTransitionWrapper
          id={id}
          currentIndex={currentIndex}
          askedOnSlideTitle={askedOnSlideTitle}
          qfaQuestion={qfaQuestion}
          numberOfUpvotes={numberOfUpvotes}
        >
          {(animatedQuestion) => {
            const width = animatedQuestion.qfaQuestion.length;
            const offset = fontOffset({
              width,
              height: 1,
              defaultOffset: -2,
              breakpoints: BREAKPOINTS,
            });
            return (
              <Box justifyContent="center" width="65%" py="space2">
                <Text fontSize="175" color="text">
                  {animatedQuestion.currentIndex + 1}/{totalNrOfQuestions}
                </Text>

                <QfaTitle
                  askedOnSlideTitle={animatedQuestion.askedOnSlideTitle}
                  displayName={displayName}
                />

                <FlexibleText
                  fontOffset={offset}
                  color="text"
                  fontWeight="regular"
                  lineHeight="heading"
                  fontFamily="heading"
                  fontSize={13}
                >
                  {animatedQuestion.qfaQuestion}
                </FlexibleText>

                {intercomEnabled && (
                  <QfaUpvotes
                    numberOfUpvotes={animatedQuestion.numberOfUpvotes}
                  />
                )}
              </Box>
            );
          }}
        </QfaTransitionWrapper>

        {!readonly && (
          <>
            {nextQuestionEnabled && (
              <Hotkey keyCode="arrowdown" action={() => nextQuestion()} />
            )}
            <ThemeBgSensitive themeBg={bg}>
              <QfaNavigationButton
                direction="down"
                onClick={() => nextQuestion()}
                isDisabled={!nextQuestionEnabled}
              />
              <SlideControlsButton
                id={id.toString()}
                hotkeys={['enter', 'space']}
                onHotkey={handleAnswerQuestion}
                onClick={handleAnswerQuestion}
                success={answeredQuestion}
                successText={translate('legacy.qfa.tab_answered')}
                slideSpecificActionsTranslationKey="mark_as_answered"
              />
            </ThemeBgSensitive>
          </>
        )}
      </Box>
    );
  },
);
